import { defineStore, acceptHMRUpdate } from 'pinia'

import { apiClient, apiBuildTxt } from "@/helpers/api.js"
import { handleApiError } from '@/services/errorHandler'
import { modifiedDateStringForBuild } from "@/helpers/datetime.js"
import { useUserStore } from './UserStore'
import { DEFAULT_TOAST_NOTIFICATION, TOAST_TYPES, TOAST_AUTO_DISMISS_MS, HELP_HUB_OPEN_KEY, HELP_HUB_WIDTH, HELP_BAR_SOURCE } from '@/constants'

export const useGlobalStore = defineStore('GlobalStore', {
  state: () => {
    return {
      windowWidth: null,
      windowHeight: null,
      sidebar: false,
      // The first new-design rail (NewSidebarV1) expanded by HOVER (an overlay,
      // not pinned open). The page doesn't reflow for it, but the fixed
      // pagination bars follow it so their left end isn't hidden under the
      // overlay. Never set by the global sidebar, which doesn't hover-expand.
      sidebarHovered: false,
      // "All Batches" slide-out panel open state (new design). Lifted here so
      // the first-run guided tour can open it for its Batches step.
      batchesPanelOpen: false,
      // During the tour's Batches step the panel would otherwise cover the
      // Batches button it's spotlighting, so we slide the header actions left of
      // the panel while this is on. Tour-only; normal panel use keeps them put.
      batchesButtonDockedForTour: false,
      // Help Hub side panel. MainLayout/HelpHub seeds it from localStorage (open by
      // default); the header toggle and useCdpSupport read it.
      helpHubOpen: false,
      // What opened the drawer, read once by the Help Hub when it captures the open event.
      // Set by the opener; `other` is the honest default for a path that doesn't say.
      helpHubSource: HELP_BAR_SOURCE.OTHER,
      helpHubWidth: HELP_HUB_WIDTH,
      // TODO(before-prod): remove these four with DevGetStartedToggles (localhost-only buttons on the new Home).
      // Each one overrides real data only while a button sets it; left at its default it changes nothing.
      // - homeState: forces the new / active Home layout; null = the real state (useHomeState).
      homeState: null,
      // - hasSale: pretends a first sale, unlocking the last two Getting Started steps.
      hasSale: false,
      // - completedSteps: Getting Started step keys to show as finished.
      completedSteps: [],
      // - freshProgress: ignores the record's progress, so every step shows as a new dealer sees it.
      freshProgress: false,
      creditsBarHidden: false,
      creditsMenuOpen: false,
      creditsCollapseDisabled: false,
      userAgent: '',
      pusherLoading: false,
      openPlansModal : false,
      openBuyScansModal: false,
      hideAdminBanner: false,
      stripe: null,
      card: null, // XXX stripe card element? need better name given our app is all about "cards". maybe stripeCard?
      sendFeedbackLoader: false,
      sendFeedbackError: false,
      hasNewVersion: false,
      errors: null,
      filters: [],
      saveActiveFilterFor: {
        activeFilters: '',
        viewOption: '',
        sort: '',
        displayColumns: '',
        columnState: ''
      },
      agBodyScroll: {
        popover: {
          old: { top: 0, left: 0 },
          new: { top: 0, left: 0 },
        },
      },
      cellsLoading: false,
      replaceImageLoader: false,
      mobileScanOpened: false,
      celloAlreadyBooted: false,
      openDialog: false,
      showAddressDialog: false,
      showAddressAlert: false,
      showPlanInfoDialog: false,
      showAddressBanner: false,
      addressWarningMessage: 'The address you entered appears to be incorrect or incomplete. Please update your address to continue.',
      toastNotification: {
        show: false,
        message: '',
        type: 'success',
        title: '',
        action: null
      },
      toastTimeoutId: null,
      toastStartTime: null,
      maintenanceMode: false,
      confirmationModal: {
        show: false,
        confirmationMessage: '',
        confirmButtonText: '',
        confirmLabel: '',
        singleOkButton: false,
        confirmAction: () => {},
        mainConfirmButtonClasses: 'primary-button',
        warningIcon: false,
        logo: null
      },

      openBatchErrorModal: false,
      maintenanceMode: false,
      videoWidgetOpen: false,
      // True while any AppDetailPanel (Order Details, future Inventory /
      // Batch detail panels) is on screen. `detailPanelWidth` is the parsed
      // pixel width of the active panel — `useCdpSupport` adds it to the
      // chat-widget's `right` offset so the bubble doesn't sit on top of
      // the panel's footer buttons.
      detailPanelOpen:  false,
      detailPanelWidth: 0,
      // Height (px) of any sticky bottom bar (currently AppPagination in
      // fixed mode). `useCdpSupport` adds this + a 12px gap to the chat
      // widget's `bottom` offset so the bubble sits above the bar instead
      // of being covered by it. 0 when no bar is mounted.
      paginationBarHeight: 0,

      /**
       * Total height of the FIXED chrome at the top of the page — the 48px
       * header row plus whatever is stacked with it: the mobile/tablet credits
       * bar, a system notification bar, the admin banner.
       *
       * `MainLayout` owns the calculation (it is the same number it pads the
       * page content by) and publishes it here, because anything else that has
       * to start BELOW the chrome needs the same figure and cannot see that
       * computed. Hard-coding 48 works only for the plainest desktop session and
       * silently covers the header the moment another bar appears.
       */
      appChromeHeight: 48,
      // How many fixed AppPagination bars are mounted. Ref-counted because
      // route changes mount the next bar before unmounting the previous one —
      // see AppPagination's `publishBar`.
      paginationBarCount: 0,
      // True while a date-range popover (AppDateRangePicker) is open.
      // `useCdpSupport` hides the floating chat widget so it can't overlap
      // the calendar, which opens near the bottom-right where the bubble sits.
      datePickerOpen: false,
      // True while a hover preview overlay (e.g. the Inventory batches panel's
      // "Batch Preview") is showing. `useCdpSupport` hides the chat bubble —
      // which is shifted left by an open detail panel — so it can't sit on top
      // of the preview. z-index alone is unreliable against the widget's
      // shadow-DOM host, hence the display:none hide path.
      batchPreviewOpen: false,
      // How many open overlays currently cover the bottom-right corner where
      // the support + Cello bubbles live (portal-mode AppMenuPanels tall enough
      // to reach it — the Columns checklist on a short screen). `useCdpSupport`
      // hides BOTH widgets while this is > 0. Ref-counted like
      // `paginationBarCount`: a plain boolean would be cleared by the first
      // overlay to close even if another is still open.
      cornerOverlayCount: 0,
    }
  },

  getters: {
    showAdminBanner() {
      const userStore = useUserStore()
      return !this.hideAdminBanner && userStore.overrideAuth
    },
  },

  actions: {
    // The dealer's own toggle / ✕. The mobile auto-close sets the flag directly, so it is not remembered.
    setHelpHubOpen(open, source = HELP_BAR_SOURCE.OTHER) {
      if (open) this.helpHubSource = source
      this.helpHubOpen = open
      localStorage.setItem(HELP_HUB_OPEN_KEY, String(open))
    },

    /**
     * Show the global bottom-left toast.
     *
     * Auto-dismisses after `TOAST_AUTO_DISMISS_MS` except for `progress`, which
     * is sticky by design — the caller swaps it for a `completed` / `error`
     * toast when the queued work reports back. Calling this again always clears
     * any pending timer first, so a progress → completed handoff can't be cut
     * short by the previous toast's timeout.
     */
    showToast({ title = '', message = '', type = TOAST_TYPES.SUCCESS, action = null } = {}) {
      if (this.toastTimeoutId) {
        clearTimeout(this.toastTimeoutId)
        this.toastTimeoutId = null
      }

      this.toastNotification = { show: true, title, message, type, action }
      this.toastStartTime = Date.now()

      if (type === TOAST_TYPES.PROGRESS) return

      this.toastTimeoutId = setTimeout(() => {
        this.dismissToast()
      }, TOAST_AUTO_DISMISS_MS)
    },

    dismissToast() {
      if (this.toastTimeoutId) {
        clearTimeout(this.toastTimeoutId)
        this.toastTimeoutId = null
      }

      this.toastNotification = { ...DEFAULT_TOAST_NOTIFICATION }
      this.toastStartTime = null
    },

    async postFeedback(body) {
      try {
        this.sendFeedbackError = false
        this.sendFeedbackLoader = true
        const { data, response } = await apiClient(false, true).post('/feedback', body)

        if (!data || !data.ok) {
          this.sendFeedbackError = true
        }

        this.sendFeedbackLoader = false
      } catch (error) {
        handleApiError(error, { component: 'GlobalStore', action: 'postFeedback' })
        this.sendFeedbackLoader = false
        this.sendFeedbackError = true
      }
    },

    async fetchBuildTxt() {
      try {
        const { data } = await apiBuildTxt().get(`/build.txt?ts=${new Date().getTime()}`)
        
        const buildAttribute = document.body.getAttribute('build')

        if (!buildAttribute || !data) return

        const modifiedDateFromServer = modifiedDateStringForBuild(data)
        const modifiedBuildAttribute = modifiedDateStringForBuild(buildAttribute)



        if (new Date(modifiedBuildAttribute) < new Date(modifiedDateFromServer)) {
          this.hasNewVersion = true
        }

      } catch (error) {
        handleApiError(error, { component: 'GlobalStore', action: 'fetchBuildTxt' })
      }
    },

    async createUtmUser(body) {
      try {
        const { data } = await apiClient().post(`/utm-user`, body)
       
        return data
      } catch (error) {
        handleApiError(error, { component: 'GlobalStore', action: 'createUtmUser' })
      }
    },

    async updateUtmUser(id, body) {
      try {
        const { data } = await apiClient().put(`/utm-user/${id}`, body)
        
        return data
      } catch (error) {
        handleApiError(error, { component: 'GlobalStore', action: 'updateUtmUser' })
      }
    }
  }
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useGlobalStore, import.meta.hot))
}
